// Copyright (c) Docugami, Inc. All rights reserved.
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from '../../store';
import { primaryTheme } from '../shell/presentation/AppStyles';
import { useDgIcons, dgIconNames } from '../../hooks/dgIcons';
import { getClusterById, getClusterIdForDocument } from '../../store/state_helpers';
import { MainMenu, MenuItem, showSettingsMenuItem } from '../shell/presentation/MainMenu'
import { Config } from '../../config';
import { useNavigationService } from '../../hooks/navigationService';
import { IContextualMenuItem } from "@fluentui/react";
import { ModalViewer } from '../shell/utils/ModalViewer';
import { useAppDispatch } from 'hooks/redux';
import { useDgAuth } from 'hooks/DgAuthentication';

export interface AaTaskpaneMenuProps {
    offestTop: string;
    open: boolean;
    closeCallback: () => void;
    documentId: string;
}

export function ServerSideRedirect(url: string): void {
    var encoded = encodeURIComponent(url);
    var redirectUrl = `${Config.apiUrl}/ClientConfiguration/RedirectToClientUrl?redirect=${encoded}`;

    var isInIframe = (window.location !== window.parent.location) ? true : false;
    if (isInIframe) {
        // Office Online
        window.open(redirectUrl);
    } else {
        // Office Rich Client
        window.location.replace(redirectUrl);
    }
}

export const AaTaskpaneMenu = (props: AaTaskpaneMenuProps): JSX.Element => {
    const { isAuthenticated } = useDgAuth();
    const [modalShowing, setModalShowing] = useState(false);
    const [legalEase, setLegalEase] = useState(<div />);
    const curClusterId = useSelector((state: AppState) => getClusterIdForDocument(state, props.documentId));
    const curCluster = useSelector((state: AppState) => getClusterById(state, curClusterId));

    const navService = useNavigationService();

    const dgIcons = useDgIcons();
    const dispatch = useAppDispatch();

    function modalDismissed(): void {
        setModalShowing(false);
        setLegalEase(<div />);
    }

    function OpenDocugamiApp(): void {
        if (!!curCluster && !!props.documentId) {
            ServerSideRedirect(`${Config.baseUrl}${navService.getUrlForDocumentDeeplink(
                curCluster?.clusterInfo.clusterId as string, props.documentId)}`);
        } else {
            ServerSideRedirect(`${Config.baseUrl}`);
        }
    }

    function OpenInWebsite(): void {
        ServerSideRedirect("https://www.docugami.com");
    }

    function getMenuEntries(): MenuItem[] {
        let manageItems: IContextualMenuItem[] = [];
        let supportItems: IContextualMenuItem[] = [];

        const logoutIcon = dgIcons.icon(dgIconNames.Logout, '', 22, 18, primaryTheme.palette.black, '');
        const manageIcon = dgIcons.icon(dgIconNames.Manage, '', 18, 25, primaryTheme.palette.black, '');
        const supportIcon = dgIcons.icon(dgIconNames.Support, '', 22, 22, primaryTheme.palette.black, '');

        manageItems.push(
            showSettingsMenuItem(dispatch)
        );

        supportItems.push(
            {
                key: 'docugami_app',
                text: 'Docugami App',
                onClick: OpenDocugamiApp
            },
            {
                key: 'docugami_website',
                text: 'Docugami Website',
                onClick: OpenInWebsite
            },
            {
                key: 'tou',
                text: 'Terms of Use',
                url: 'https://www.docugami.com/terms'
            },
            {
                key: 'privacy_policy',
                text: 'Privacy Policy',
                url: 'https://www.docugami.com/privacy'
            }
        );

        let final_menu: MenuItem[] = [
            {
                title: 'Manage Account',
                icon: manageIcon,
                listItems: manageItems,
            }
            ,
            {
                title: 'Support',
                icon: supportIcon,
                listItems: supportItems
            }
        ]

        // show signout for non SSO signins
        if (isAuthenticated) {
            final_menu.push(
                {
                    title: 'Log Out',
                    icon: logoutIcon,
                    listItems: [],
                    onClick: () => navService.logout()
                }
            )
        }

        return final_menu;
    }

    return (
        <div style={{ width: "100%", display: "flex", flexDirection: "row" }}>
            <div style={{ flex: 1 }}></div>
            <MainMenu
                offestTop={props.offestTop}
                open={props.open}
                closeCallback={props.closeCallback}
                menuItems={getMenuEntries()} />
            <ModalViewer
                title=''
                hidden={!modalShowing}
                dismissedCallback={modalDismissed}>
                {legalEase}
            </ModalViewer>
        </div>);
}
